import React, { useState, useMemo, useEffect } from "react";
import DashboardFilters from "./DashboardFilters";
import './DataPoints.css';

const rowsPerPage = 10;

const DataPoints = ({ sourceName, fetchDataFn, onTotalsCalculated, renderVisuals, renderTable, exportCSVFn }) => {
    const [records, setRecords] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [minDate, setMinDate] = useState("");
    const [maxDate, setMaxDate] = useState("");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [appliedStartDate, setAppliedStartDate] = useState("");
    const [appliedEndDate, setAppliedEndDate] = useState("");
    const [selectedState, setSelectedState] = useState("");
    const [selectedDistrict, setSelectedDistrict] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [exportingFormat, setExportingFormat] = useState(null);

    const [chartData, setChartData] = useState(null);

    // Fetch Live Data from server with pagination and filters
    useEffect(() => {
        const fetchMasterData = async () => {
            setLoading(true);
            try {
                const response = await fetchDataFn(currentPage, rowsPerPage, appliedStartDate, appliedEndDate, selectedState, selectedDistrict);
                if (response && response.success) {
                    setRecords(response.data || []);
                    setTotalCount(response.count || 0);
                    setTotalPages(response.totalPages || 1);
                    
                    // Only update min/max bounds if provided by API (usually represents global bounds)
                    if (response.minDate) setMinDate(response.minDate);
                    if (response.maxDate) setMaxDate(response.maxDate);
                    
                    if (response.chartData) {
                        setChartData(response.chartData);
                    }
                    setError("");
                } else {
                    throw new Error("Invalid API response format");
                }
            } catch (err) {
                console.error(`Failed to fetch live ${sourceName} data:`, err);
                setError(`Live server offline (${err.message || 'Network Error'})`);
                setRecords([]);
            } finally {
                setLoading(false);
            }
        };
        fetchMasterData();
    }, [fetchDataFn, sourceName, currentPage, appliedStartDate, appliedEndDate, selectedState, selectedDistrict]);

    // Overall Severity Totals for AdminDashboard Top Cards
    const overallTotals = useMemo(() => {
        if (chartData && chartData.overallTotals) {
            const ot = chartData.overallTotals;
            return {
                total_normal: ot.total_normal ?? ot.total_non_anaemic ?? 0,
                total_mild: ot.total_mild ?? 0,
                total_moderate: ot.total_moderate ?? 0,
                total_severe: ot.total_severe ?? 0
            };
        }
        return { total_normal: 0, total_mild: 0, total_moderate: 0, total_severe: 0 };
    }, [chartData]);

    useEffect(() => {
        if (onTotalsCalculated) {
            onTotalsCalculated(overallTotals);
        }
    }, [overallTotals, onTotalsCalculated]);

    const handleExportCSV = () => {
        // With server-side pagination, 'records' only contains 10 rows.
        // In the future, this should call a dedicated API endpoint for exporting.
        let localData = [...records];

        if (exportCSVFn) {
            exportCSVFn(localData, sourceName);
            return;
        }

        // Fallback or generic export (you can remove this if all wrappers provide exportCSVFn)
        const headers = ["ID", "Report Date", "District", "District LGD", "State", "State LGD"];
        const fields = ["id", "report_date", "district_name", "district_lgd", "state_name", "state_lgd"];

        const csvRows = [
            headers.join(","),
            ...localData.map((r) => {
                return fields.map((f) => {
                    const val = r[f] !== null && r[f] !== undefined ? r[f] : "";
                    const strVal = String(val).replace(/"/g, '""');
                    return strVal.includes(",") || strVal.includes("\n") || strVal.includes('"') ? `"${strVal}"` : strVal;
                }).join(",");
            })
        ];

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        const dateStr = new Date().toISOString().split('T')[0];
        link.setAttribute("download", `${sourceName.toLowerCase()}_explorer_export_${dateStr}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleExportApi = async (format) => {
        setExportingFormat(format);
        try {
            if (format === "csv") {
                handleExportCSV();
            } else {
                alert(`API export for ${format} is currently unsupported by the server.`);
            }
        } finally {
            setExportingFormat(null);
        }
    };

    const handleExportCSVFallback = () => {
        if (error) {
            handleExportCSV();
        } else {
            handleExportApi("csv");
        }
    };

    return (
        <div className="glass-card card border-0 shadow-sm p-4 d-flex flex-column gap-4">
            <DashboardFilters
                sourceName={sourceName}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                setAppliedStartDate={setAppliedStartDate}
                setAppliedEndDate={setAppliedEndDate}
                minDate={minDate}
                maxDate={maxDate}
                setCurrentPage={setCurrentPage}
                selectedState={selectedState}
                setSelectedState={setSelectedState}
                selectedDistrict={selectedDistrict}
                setSelectedDistrict={setSelectedDistrict}
                handleExportCSVFallback={handleExportCSVFallback}
                exportingFormat={exportingFormat}
            />

            {renderVisuals && renderVisuals(chartData, loading, overallTotals)}
            {renderTable && renderTable(records, loading, currentPage, setCurrentPage, totalCount, totalPages, rowsPerPage)}
        </div>
    );
};

export default DataPoints;